import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Plus, 
  Send, 
  Download, 
  Check, 
  Clock, 
  AlertTriangle,
  Loader2,
  RefreshCw,
  Settings,
  Bell,
  BarChart3,
  Trash2,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  AlertCircle,
  Undo2,
  Ban
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActionBtn = ({ onClick, disabled, icon: Icon, label, variant = 'default', testId }) => {
  const variants = {
    default: 'text-muted-foreground hover:text-foreground hover:bg-muted',
    primary: 'text-primary/70 hover:text-primary hover:bg-primary/10',
    success: 'text-green-600/70 hover:text-green-600 hover:bg-green-600/10',
    warning: 'text-amber-500/70 hover:text-amber-500 hover:bg-amber-500/10',
    purple: 'text-purple-500/70 hover:text-purple-500 hover:bg-purple-500/10',
    danger: 'text-red-500/70 hover:text-red-500 hover:bg-red-500/10',
  };
  return (
    <div className="relative group">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`p-2 rounded-md transition-colors disabled:opacity-40 ${variants[variant]}`}
        data-testid={testId}
      >
        <Icon size={18} />
      </button>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
        {label}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
      </span>
    </div>
  );
};

export default function InvoicesPage() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('invoice_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const [generating, setGenerating] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [unsentCount, setUnsentCount] = useState(0);
  const [showSendAllConfirm, setShowSendAllConfirm] = useState(false);
  const [report, setReport] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMemberSearch, setCreateMemberSearch] = useState('');
  const [createMembers, setCreateMembers] = useState([]);
  const [createSelectedMember, setCreateSelectedMember] = useState(null);
  const [createType, setCreateType] = useState('annual');
  const [createAmount, setCreateAmount] = useState('');
  const [createNote, setCreateNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchInvoices();
    fetchReport();
    fetchUnsentCount();
  }, [year, statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      let url = `${API_URL}/api/admin/invoices?year=${year}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInvoices(response.data.invoices);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/invoices/report/summary?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    }
  };

  const fetchUnsentCount = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/invoices/unsent-count?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnsentCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unsent count:', error);
    }
  };

  const handleGenerateBatch = async () => {
    setGenerating(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/invoices/generate-batch?year=${year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.created} Rechnungen erstellt, ${response.data.skipped} übersprungen`);
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setGenerating(false);
    }
  };

  const handleSyncStatusFromMonday = async () => {
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/invoices/sync-status-from-monday?year=${year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.updated} Rechnungen als bezahlt markiert (aus Monday.com)`);
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Sync');
    }
  };

  const handleSendAllOpen = async () => {
    setSendingAll(true);
    setShowSendAllConfirm(false);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/invoices/send-all-open?year=${year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.sent > 0) {
        toast.success(`${response.data.sent} Rechnungen erfolgreich gesendet`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} Rechnungen konnten nicht gesendet werden`);
      }
      if (response.data.sent === 0 && response.data.failed === 0) {
        toast.info('Keine ungesendeten Rechnungen gefunden');
      }
      
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingAll(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/invoices/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.sent} Mahnungen gesendet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/invoices/${invoiceId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    const token = getToken();
    
    try {
      await axios.post(
        `${API_URL}/api/admin/invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Rechnung gesendet');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    const token = getToken();
    
    try {
      await axios.put(
        `${API_URL}/api/admin/invoices/${invoiceId}`,
        { status: 'paid' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Als bezahlt markiert (wird an Monday.com synchronisiert)');
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleMarkOpen = async (invoiceId) => {
    const token = getToken();
    
    try {
      await axios.put(
        `${API_URL}/api/admin/invoices/${invoiceId}`,
        { status: 'open' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status auf offen gesetzt');
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleCancel = async (invoice) => {
    const note = window.prompt(
      `Rechnung ${invoice.invoice_number} für ${invoice.member_name} stornieren?\n\nGrund (optional):`,
      'Mitglied ausgetreten'
    );
    if (note === null) return;
    
    const token = getToken();
    try {
      await axios.put(
        `${API_URL}/api/admin/invoices/${invoice.id}`,
        { status: 'cancelled', note: note ? `Storniert – ${note}` : 'Storniert' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rechnung storniert');
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (error) {
      toast.error('Fehler beim Stornieren');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Rechnung wirklich löschen?')) return;
    
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Rechnung gelöscht');
      fetchInvoices();
      fetchReport();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleRefund = async (invoice) => {
    const reason = window.prompt(
      `Rückerstattung für ${invoice.member_name}\nBetrag: CHF ${(invoice.total_amount || invoice.amount)?.toFixed(2)}\n\nGrund für die Rückerstattung (optional):`,
      ''
    );
    
    if (reason === null) return; // User cancelled
    
    setRefunding(invoice.id);
    const token = getToken();
    
    try {
      await axios.post(
        `${API_URL}/api/payments/invoice/${invoice.id}/refund`,
        { reason: reason || 'Rückerstattung durch Admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rückerstattung erfolgreich! E-Mail wurde an ${invoice.member_email} gesendet.`);
      fetchInvoices();
      fetchReport();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler bei der Rückerstattung';
      toast.error(message);
    } finally {
      setRefunding(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
  };

  // Member search for create modal
  useEffect(() => {
    if (!createMemberSearch || createMemberSearch.length < 2) {
      setCreateMembers([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setLoadingMembers(true);
      try {
        const res = await axios.get(`${API_URL}/api/members`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const all = res.data?.members || res.data || [];
        const q = createMemberSearch.toLowerCase();
        setCreateMembers(all.filter(m =>
          (m.name || '').toLowerCase().includes(q) ||
          (m.email || '').toLowerCase().includes(q)
        ).slice(0, 10));
      } catch { /* ignore */ }
      finally { setLoadingMembers(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [createMemberSearch]);

  const handleCreateInvoice = async () => {
    if (!createSelectedMember) { toast.error('Bitte Mitglied auswählen'); return; }
    setCreating(true);
    try {
      const payload = {
        member_id: createSelectedMember.id,
        year,
        invoice_type: createType,
        note: createNote || undefined,
      };
      if (createAmount) payload.amount = parseFloat(createAmount);
      
      const res = await axios.post(`${API_URL}/api/admin/invoices`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Rechnung ${res.data.invoice_number} erstellt`);
      setShowCreateModal(false);
      setCreateSelectedMember(null);
      setCreateMemberSearch('');
      setCreateAmount('');
      setCreateNote('');
      setCreateType('annual');
      fetchInvoices();
      fetchReport();
      fetchUnsentCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setCreating(false); }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount || 0);
  };

  // Filter and sort invoices
  const filteredAndSortedInvoices = useMemo(() => {
    let result = [...invoices];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv => 
        inv.member_name?.toLowerCase().includes(query) ||
        inv.member_email?.toLowerCase().includes(query) ||
        inv.invoice_number?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle different field types
      if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'member_name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [invoices, searchQuery, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="text-primary" size={28} />
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
              Rechnungen
            </h1>
          </div>
          <Link to="/admin/invoices/settings">
            <Button variant="outline" className="rounded-none">
              <Settings className="mr-2 h-4 w-4" />
              Einstellungen
            </Button>
          </Link>
        </div>

        {/* Stats */}
        {report && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <div className="bg-card border border-border p-3 text-center">
              <p className="text-xl font-bold text-foreground" data-testid="stat-total-members">{report.total_members ?? '-'}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Mitglieder</p>
            </div>
            <div className="bg-card border border-border p-3 text-center">
              <p className="text-xl font-bold text-foreground" data-testid="stat-billable-members">{report.billable_members ?? '-'}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Zahlungspfl.</p>
            </div>
            <div className="bg-card border border-border p-3 text-center">
              <p className="text-xl font-bold text-foreground" data-testid="stat-total-invoices">{report.total_invoices}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Rechnungen</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 p-3 text-center">
              <p className="text-xl font-bold text-green-600" data-testid="stat-paid">{report.paid_invoices}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Bezahlt</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 text-center">
              <p className="text-xl font-bold text-yellow-600" data-testid="stat-open">{report.open_invoices}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Offen</p>
            </div>
            <div className="bg-card border border-border p-3 text-center">
              <p className="text-xl font-bold text-foreground" data-testid="stat-total-amount">{formatCurrency(report.total_amount)}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Gesamtbetrag</p>
            </div>
            <div className="bg-primary/10 border border-primary/30 p-3 text-center">
              <p className="text-xl font-bold text-primary" data-testid="stat-collection-rate">{report.collection_rate?.toFixed(0)}%</p>
              <p className="text-[10px] text-muted-foreground uppercase">Einzugsquote</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Suche..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-48 md:w-64 bg-secondary/50 border-border rounded-none"
              data-testid="invoice-search"
            />
          </div>

          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="h-10 px-3 bg-secondary/50 border border-border rounded-none text-foreground"
          >
            {[2024, 2025, 2026, 2027, 2028].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 bg-secondary/50 border border-border rounded-none text-foreground"
          >
            <option value="">Alle Status</option>
            <option value="open">Offen</option>
            <option value="paid">Bezahlt</option>
            <option value="cancelled">Storniert</option>
            <option value="refunded">Rückerstattet</option>
          </select>

          <div className="relative group">
            <Button
              onClick={handleGenerateBatch}
              disabled={generating}
              className="bg-primary hover:bg-red-700 text-white rounded-none text-xs sm:text-sm"
              data-testid="generate-batch-btn"
            >
              {generating ? (
                <Loader2 className="mr-1 sm:mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-1 sm:mr-2 h-4 w-4" />
              )}
              <span className="hidden sm:inline">Rechnungen </span>erstellen
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Rechnungen für alle zahlungspflichtigen Mitglieder erstellen
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>

          <div className="relative group">
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="outline"
              className="rounded-none text-xs sm:text-sm"
              data-testid="create-single-invoice-btn"
            >
              <FileText className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Einzelrechnung</span>
              <span className="sm:hidden">Einzel</span>
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Einzelrechnung für ein Mitglied erstellen (Jahresbeitrag oder Eintrittsgebühr)
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>

          <div className="relative group">
            <Button
              onClick={() => setShowSendAllConfirm(true)}
              disabled={sendingAll || unsentCount === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-none text-xs sm:text-sm"
              data-testid="send-all-btn"
            >
              {sendingAll ? (
                <Loader2 className="mr-1 sm:mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-1 sm:mr-2 h-4 w-4" />
              )}
              <span className="hidden sm:inline">Alle </span>senden {unsentCount > 0 && `(${unsentCount})`}
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Alle offenen, noch nicht versendeten Rechnungen per E-Mail senden
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>

          <div className="relative group">
            <Button
              variant="outline"
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="rounded-none text-xs sm:text-sm"
              data-testid="send-reminders-btn"
            >
              {sendingReminders ? (
                <Loader2 className="mr-1 sm:mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Bell className="mr-1 sm:mr-2 h-4 w-4" />
              )}
              <span className="hidden md:inline">Mahnungen</span>
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Zahlungserinnerung an überfällige Rechnungen senden
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>

          <div className="relative group">
            <Button
              variant="outline"
              onClick={handleSyncStatusFromMonday}
              className="rounded-none text-xs sm:text-sm hidden sm:flex"
              data-testid="sync-monday-btn"
            >
              <RefreshCw className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden lg:inline">Monday Sync</span>
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Zahlungsstatus aus Monday.com synchronisieren
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>

          <div className="relative group">
            <Button
              variant="outline"
              onClick={() => { fetchInvoices(); fetchReport(); }}
              className="rounded-none"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-zinc-900 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
              Aktualisieren
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </span>
          </div>
        </div>

        {/* Invoice List */}
        <div className="bg-card border border-border">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredAndSortedInvoices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
              {searchQuery ? (
                <p>Keine Rechnungen für "{searchQuery}" gefunden</p>
              ) : (
                <>
                  <p>Keine Rechnungen für {year}</p>
                  <p className="text-sm mt-2">
                    Klicken Sie auf "Rechnungen erstellen" um Rechnungen für alle zahlungspflichtigen Mitglieder zu generieren.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto overflow-y-visible">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('invoice_number')}
                      >
                        <span className="flex items-center gap-1">
                          Rechnung <SortIcon field="invoice_number" />
                        </span>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('member_name')}
                      >
                        <span className="flex items-center gap-1">
                          Mitglied <SortIcon field="member_name" />
                        </span>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('amount')}
                      >
                        <span className="flex items-center gap-1">
                          Betrag <SortIcon field="amount" />
                        </span>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('status')}
                      >
                        <span className="flex items-center gap-1">
                          Status <SortIcon field="status" />
                        </span>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('due_date')}
                      >
                        <span className="flex items-center gap-1">
                          Fällig <SortIcon field="due_date" />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Gesendet
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('paid_at')}
                      >
                        <span className="flex items-center gap-1">
                          Bezahlt am <SortIcon field="paid_at" />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredAndSortedInvoices.map((invoice) => {
                      const isOverdue = invoice.status === 'open' && invoice.sent_at && new Date(invoice.due_date) < new Date();
                      
                      return (
                        <tr key={invoice.id} className="hover:bg-muted/20">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-foreground">
                            {invoice.invoice_number}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-foreground">{invoice.member_name}</div>
                            <div className="text-xs text-muted-foreground">{invoice.member_email}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                            {formatCurrency(invoice.total_amount || invoice.amount)}
                            {invoice.entry_fee > 0 && (
                              <span className="ml-1 text-[10px] text-muted-foreground" title={`Beitrag: ${formatCurrency(invoice.amount)} + Eintrittsgebühr: ${formatCurrency(invoice.entry_fee)}`}>
                                *
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {invoice.status === 'paid' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-600">
                                <Check size={12} />
                                Bezahlt
                              </span>
                            ) : invoice.status === 'refunded' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-600">
                                <Undo2 size={12} />
                                Rückerstattet
                              </span>
                            ) : invoice.status === 'cancelled' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-500 line-through">
                                Storniert
                              </span>
                            ) : isOverdue ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-600">
                                <AlertTriangle size={12} />
                                Überfällig
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-600">
                                <Clock size={12} />
                                Offen
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(invoice.due_date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                            {invoice.sent_at ? formatDate(invoice.sent_at) : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                            {invoice.paid_at ? formatDate(invoice.paid_at) : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                            <div className="flex items-center justify-end gap-0.5">
                              {invoice.status !== 'cancelled' && (
                                <ActionBtn
                                  onClick={() => handleDownloadPdf(invoice.id)}
                                  icon={Download}
                                  label="PDF herunterladen"
                                  testId={`download-${invoice.id}`}
                                />
                              )}
                              {invoice.status === 'open' && (
                                <ActionBtn
                                  onClick={() => handleSendInvoice(invoice.id)}
                                  icon={Send}
                                  label="Per E-Mail senden"
                                  variant="primary"
                                  testId={`send-${invoice.id}`}
                                />
                              )}
                              {invoice.status === 'open' ? (
                                <>
                                  <ActionBtn
                                    onClick={() => handleMarkPaid(invoice.id)}
                                    icon={Check}
                                    label="Als bezahlt markieren"
                                    variant="success"
                                    testId={`mark-paid-${invoice.id}`}
                                  />
                                  <ActionBtn
                                    onClick={() => handleCancel(invoice)}
                                    icon={Ban}
                                    label="Stornieren"
                                    variant="warning"
                                    testId={`cancel-${invoice.id}`}
                                  />
                                </>
                              ) : invoice.status === 'paid' && invoice.stripe_session_id ? (
                                <ActionBtn
                                  onClick={() => handleRefund(invoice)}
                                  disabled={refunding === invoice.id}
                                  icon={refunding === invoice.id ? Loader2 : Undo2}
                                  label="Rückerstattung (Stripe)"
                                  variant="purple"
                                  testId={`refund-${invoice.id}`}
                                />
                              ) : invoice.status === 'paid' ? (
                                <ActionBtn
                                  onClick={() => handleMarkOpen(invoice.id)}
                                  icon={Clock}
                                  label="Wieder auf offen setzen"
                                  variant="warning"
                                  testId={`mark-open-${invoice.id}`}
                                />
                              ) : null}
                              <ActionBtn
                                onClick={() => handleDelete(invoice.id)}
                                icon={Trash2}
                                label="Löschen"
                                variant="danger"
                                testId={`delete-${invoice.id}`}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-border">
                {filteredAndSortedInvoices.map((invoice) => {
                  const isOverdue = invoice.status === 'open' && new Date(invoice.due_date) < new Date();
                  
                  return (
                    <div key={invoice.id} className="p-4 space-y-3">
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-foreground">{invoice.member_name}</p>
                          <p className="text-xs text-muted-foreground">{invoice.member_email}</p>
                        </div>
                        {invoice.status === 'paid' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-500/20 text-green-600">
                            <Check size={12} />
                            Bezahlt
                          </span>
                        ) : invoice.status === 'refunded' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-500/20 text-purple-600">
                            <Undo2 size={12} />
                            Rückerstattet
                          </span>
                        ) : isOverdue ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/20 text-red-600">
                            <AlertTriangle size={12} />
                            Überfällig
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-500/20 text-yellow-600">
                            <Clock size={12} />
                            Offen
                          </span>
                        )}
                      </div>
                      
                      {/* Details Row */}
                      <div className="flex items-center justify-between text-sm">
                        <div className="space-y-1">
                          <p><span className="text-muted-foreground">Nr:</span> {invoice.invoice_number}</p>
                          <p><span className="text-muted-foreground">Fällig:</span> {formatDate(invoice.due_date)}</p>
                        </div>
                        <p className="text-lg font-bold text-foreground">{formatCurrency(invoice.total_amount || invoice.amount)}</p>
                      </div>
                      
                      {/* Actions Row */}
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          {invoice.paid_at ? `Bezahlt: ${formatDate(invoice.paid_at)}` : invoice.sent_at ? `Gesendet: ${formatDate(invoice.sent_at)}` : 'Noch nicht gesendet'}
                        </p>
                        <div className="flex items-center gap-1">
                          <ActionBtn
                            onClick={() => handleDownloadPdf(invoice.id)}
                            icon={Download}
                            label="PDF"
                          />
                          {invoice.status === 'open' && (
                            <ActionBtn
                              onClick={() => handleSendInvoice(invoice.id)}
                              icon={Send}
                              label="Senden"
                              variant="primary"
                            />
                          )}
                          {invoice.status === 'open' ? (
                            <>
                              <ActionBtn
                                onClick={() => handleMarkPaid(invoice.id)}
                                icon={Check}
                                label="Bezahlt"
                                variant="success"
                              />
                              <ActionBtn
                                onClick={() => handleCancel(invoice)}
                                icon={Ban}
                                label="Stornieren"
                                variant="warning"
                              />
                            </>
                          ) : invoice.status === 'paid' && invoice.stripe_session_id ? (
                            <ActionBtn
                              onClick={() => handleRefund(invoice)}
                              disabled={refunding === invoice.id}
                              icon={refunding === invoice.id ? Loader2 : Undo2}
                              label="Rückerstatten"
                              variant="purple"
                            />
                          ) : invoice.status === 'paid' ? (
                            <ActionBtn
                              onClick={() => handleMarkOpen(invoice.id)}
                              icon={Clock}
                              label="Offen"
                              variant="warning"
                            />
                          ) : null}
                          <ActionBtn
                            onClick={() => handleDelete(invoice.id)}
                            icon={Trash2}
                            label="Löschen"
                            variant="danger"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Send All Confirmation Modal */}
      {showSendAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-500/20 rounded-full">
                <AlertCircle className="text-amber-500" size={24} />
              </div>
              <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase">
                Alle Rechnungen senden?
              </h3>
            </div>
            
            <div className="mb-6 space-y-3">
              <p className="text-foreground">
                Sie sind dabei, <strong className="text-primary">{unsentCount} Rechnungen</strong> per E-Mail zu versenden.
              </p>
              <p className="text-sm text-amber-500 bg-amber-500/10 p-2 border border-amber-500/30">
                <strong>Test-Modus aktiv:</strong> Alle E-Mails werden nur an Sie (Admin) gesendet.
              </p>
              <p className="text-sm text-muted-foreground">
                Dies betrifft alle offenen Rechnungen für {year}, die noch nicht gesendet wurden. 
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSendAllConfirm(false)}
                className="rounded-none"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSendAllOpen}
                className="bg-primary hover:bg-red-700 text-white rounded-none"
              >
                <Mail className="mr-2 h-4 w-4" />
                Ja, alle senden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Single Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-card border border-border p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-wide mb-4">
              Einzelrechnung erstellen
            </h3>

            <div className="space-y-4">
              {/* Member search */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Mitglied *</label>
                {createSelectedMember ? (
                  <div className="flex items-center justify-between bg-muted/30 border border-border p-3">
                    <div>
                      <p className="text-sm font-medium">{createSelectedMember.name}</p>
                      <p className="text-xs text-muted-foreground">{createSelectedMember.email}</p>
                    </div>
                    <button onClick={() => { setCreateSelectedMember(null); setCreateMemberSearch(''); }} className="text-muted-foreground hover:text-foreground">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      value={createMemberSearch}
                      onChange={e => setCreateMemberSearch(e.target.value)}
                      placeholder="Name oder E-Mail suchen..."
                      className="rounded-none"
                      data-testid="create-invoice-member-search"
                    />
                    {loadingMembers && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
                    {createMembers.length > 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 bg-card border border-border shadow-lg max-h-48 overflow-y-auto">
                        {createMembers.map(m => (
                          <button
                            key={m.id}
                            onClick={() => { setCreateSelectedMember(m); setCreateMembers([]); setCreateMemberSearch(''); }}
                            className="block w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors"
                          >
                            <p className="text-sm font-medium">{m.name}</p>
                            <p className="text-xs text-muted-foreground">{m.email}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Invoice type */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Rechnungstyp</label>
                <select
                  value={createType}
                  onChange={e => { setCreateType(e.target.value); setCreateAmount(''); }}
                  className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none"
                  data-testid="create-invoice-type"
                >
                  <option value="annual">Jahresbeitrag</option>
                  <option value="entry_fee">Eintrittsgebühr</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {createType === 'annual'
                    ? 'Der Betrag wird automatisch aus den Einstellungen oder dem Mitgliedsprofil übernommen.'
                    : 'Der Betrag wird aus den Rechnungseinstellungen (Eintrittsgebühr) übernommen.'}
                </p>
              </div>

              {/* Year */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Jahr</label>
                <select
                  value={year}
                  className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none"
                  disabled
                >
                  <option value={year}>{year}</option>
                </select>
              </div>

              {/* Amount override */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Betrag (CHF) – optional überschreiben</label>
                <Input
                  value={createAmount}
                  onChange={e => setCreateAmount(e.target.value)}
                  placeholder="Automatisch aus Einstellungen"
                  type="number"
                  className="rounded-none"
                  data-testid="create-invoice-amount"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Bemerkung (optional)</label>
                <Input
                  value={createNote}
                  onChange={e => setCreateNote(e.target.value)}
                  placeholder="z.B. Sonderbeitrag"
                  className="rounded-none"
                  data-testid="create-invoice-note"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-none flex-1">Abbrechen</Button>
              <Button
                onClick={handleCreateInvoice}
                disabled={creating || !createSelectedMember}
                className="bg-primary hover:bg-red-700 text-white rounded-none flex-1"
                data-testid="create-invoice-submit-btn"
              >
                {creating ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                Erstellen
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
